import { EntityLink } from 'components/entity-link';
import type { PartyLink } from '../../../types';
import { occurrenceKeys } from 'utils/occurrence-keys';
import { EMPTY_CELL } from 'utils/empty-cell';
import styles from '../expand.module.scss';

/**
 * One party: an entity named the standard way and linked with its hover card,
 * or a free-text name (a vendor or manual source) as plain text.
 * @param {object} props The cell.
 * @param {PartyLink} props.party The party.
 * @return {JSX.Element} The party.
 */
export function PartyCell({ party }: { party: PartyLink }) {
  if (!party.name?.trim())
    return <span className={styles.muted}>{EMPTY_CELL}</span>;
  return <EntityLink id={party.entityId} name={party.name} hover />;
}

interface PartyCellListProps {
  parties: PartyLink[];
  /** Caps the visible parties, the rest behind a "Show more +(N)" button. */
  max?: number;
  /** Replaces the em dash shown for no parties (e.g. "Not Available"). */
  emptyLabel?: string;
  /** Called by the overflow button, e.g. to open the card's Parties. */
  onShowMore?: () => void;
}

/**
 * A stacked list of parties, each a PartyCell.
 * @param {PartyCellListProps} props The list.
 * @return {JSX.Element} The parties.
 */
export function PartyCellList({
  parties,
  max,
  emptyLabel,
  onShowMore,
}: PartyCellListProps) {
  if (parties.length === 0)
    return <span className={styles.muted}>{emptyLabel ?? EMPTY_CELL}</span>;
  const visible = max ? parties.slice(0, max) : parties;
  const remaining = parties.length - visible.length;
  const keys = occurrenceKeys(
    visible.map((party) => `${party.entityId ?? ''}:${party.name ?? ''}`)
  );
  return (
    <>
      {visible.map((party, i) => (
        <div key={keys[i]}>
          <PartyCell party={party} />
        </div>
      ))}
      {remaining > 0 && (
        <button
          type="button"
          className={styles.showMore}
          onClick={(e) => {
            e.stopPropagation();
            onShowMore?.();
          }}
        >
          Show more +({remaining})
        </button>
      )}
    </>
  );
}
